// @flow
import React from 'react';
import type { ChildrenArray } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import {
  productColor,
  productColorSunflower,
  fontFamilies,
} from '../../designSystem.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import type { MathString } from '../../types.js';
import { mathStringChecker } from '../../types.js';

type QuestionsChildrenArray = ChildrenArray<
  | React$Element<typeof INVQuestionsPreamble>
  | React$Element<typeof INVQuestion>,
>;

function InternalQuestionBox(props: {|
  title: string,
  children: QuestionsChildrenArray,
|}) {
  const { title } = props;
  const children = React.Children.toArray(props.children);
  const [maybePreamble, ...rest] = children;
  const hasPreamble = maybePreamble.type.name === INVQuestionsPreamble.name;
  const questions = hasPreamble ? rest : children;
  return (
    <div
      style={{
        backgroundColor: productColor.aliceBlue,
        color: productColorSunflower.grey,
        borderTop: '2px solid',
        borderBottom: '2px solid',
        borderColor: productColor.matisse,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        rowGap: 16,
      }}
    >
      <div
        style={{
          fontFamily: fontFamilies.gilroy.name,
          fontWeight: fontFamilies.gilroy.weights.extraBold,
          fontSize: 20,
        }}
      >
        {title}
      </div>
      {hasPreamble && maybePreamble}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          paddingLeft: 16,
        }}
      >
        {questions.map((question, idx) => (
          <div style={{ display: 'flex', gap: 8 }} key={idx}>
            <div
              style={{
                display: 'flex',
                flexShrink: 0,
                justifyContent: 'flex-end',
                width: 12,
                fontWeight: 600,
              }}
            >
              {idx + 1}.
            </div>
            <div>{question}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const questionsSpec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVQuestionsPreamble),
    },
    {
      min: 1,
      max: Infinity,
      elementType: t.elementType(INVQuestion),
    },
  ),
};

export function INVInvestigateQuestions(props: {|
  children: QuestionsChildrenArray,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVInvestigateQuestions,
  );
  if (failedValidation) return null;
  return (
    <InternalQuestionBox title="Investigate">
      {props.children}
    </InternalQuestionBox>
  );
}
INVInvestigateQuestions.spec = questionsSpec;

export function INVDiscussionQuestions(props: {|
  children: QuestionsChildrenArray,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVDiscussionQuestions,
  );
  if (failedValidation) return null;

  return (
    <InternalQuestionBox title="Discussion">
      {props.children}
    </InternalQuestionBox>
  );
}
INVDiscussionQuestions.spec = questionsSpec;

export function INVQuestionsPreamble(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVQuestionsPreamble);
  if (failedValidation) return null;

  return <div>{children}</div>;
}
INVQuestionsPreamble.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVQuestion(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { children } = props;

  const { failedValidation } = usePropsValidation(props, INVQuestion);
  if (failedValidation) return null;

  return children;
}
INVQuestion.spec = {
  children: t.childrenArray(mathStringChecker),
};
